// hooks/market/useMyProduct.ts
//
// Charge UN produit précis appartenant à l'utilisateur connecté, par son
// id, tous statuts confondus (draft/published/disabled/archived) — pour
// l'écran d'édition complète app/(tabs)/market/gerer-produit/[productId].tsx.
//
// Distinct de :
//   - useMyShopProducts (liste paginée des produits d'UNE boutique)
//   - get-product (public, published only, utilisé par le fil du marché)
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-my-product', { product_id }
// (voir backend/handleMarket.ts → getMyProduct)
//
// Se recharge automatiquement si productId change. Expose refresh() pour
// recharger manuellement après une sauvegarde côté écran si besoin (pas
// strictement nécessaire si l'écran garde son propre état local des
// champs, mais utile pour resynchroniser après un upload d'image externe
// via ImagePickerField).

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type MyProductStatus = 'draft' | 'published' | 'disabled' | 'archived';
export type MyProductCondition = 'new' | 'used' | 'refurbished';

export interface MyProduct {
  id: string;
  shop_id: string;
  category_id: string;
  sku: string | null;
  name: string;
  slug: string;
  description_short: string | null;
  description: string | null;
  brand: string | null;
  condition: MyProductCondition;
  cover_image_url: string | null;
  image_2_url: string | null;
  image_3_url: string | null;
  price: number;
  promo_price: number | null;
  currency_code: string;
  stock_quantity: number;
  status: MyProductStatus;
  is_featured: boolean;
  show_whatsapp_business_button: boolean;
  whatsapp_business_phone_override: string | null;
  show_platform_chat_button: boolean;
  show_buy_button: boolean;
  buy_url: string | null;
  show_learn_more_button: boolean;
  learn_more_url: string | null;
  show_call_button: boolean;
  call_phone: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  extra_data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export function useMyProduct(productId: string | undefined) {
  const [product, setProduct] = useState<MyProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!productId?.trim()) {
      setLoading(false);
      setError('product_id manquant');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-my-product',
          access_token: token,
          product_id:   productId,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger le produit');
        setProduct(null);
        return;
      }

      setProduct(data.product as MyProduct);
    } catch (err) {
      console.error('[useMyProduct]', err);
      setError('network_error');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  return { product, loading, error, refresh: load };
}
